<template>
  <div class="task-subtasks">
    <div class="task-subtasks__header">
      <h3 class="task-subtasks__title">Sub-tasks ({{ subtasks.length }})</h3>
      <themed-button size="sm" @click="startInlineCreate" v-if="canEdit && !showInlineCreate">
        <theme-icon icon="plus-icon" type="icons" classes="w-3 h-3" />
        <span>Add Sub-task</span>
      </themed-button>
    </div>

    <!-- Inline Create Form -->
    <div v-if="showInlineCreate" class="task-subtasks__inline-create">
      <div class="task-subtasks__inline-form">
        <themed-select
          v-model="inlineForm.status_id"
          :options="statusOptions"
          placeholder="Status"
          size="sm"
          class="task-subtasks__inline-status"
        />
        <input
          ref="inlineNameInput"
          v-model="inlineForm.title"
          type="text"
          placeholder="Enter sub-task name"
          class="task-subtasks__inline-input"
          @keydown.enter="saveInlineSubtask"
          @keydown.esc="cancelInlineCreate"
        />
        <themed-select
          v-model="inlineForm.priority_id"
          :options="priorityOptions"
          placeholder="Priority"
          size="sm"
          class="task-subtasks__inline-priority"
        />
        <div class="task-subtasks__inline-actions">
          <themed-button size="sm" @click="saveInlineSubtask" :disabled="!inlineForm.title || inlineLoading">
            <theme-icon v-if="inlineLoading" icon="loading" type="icons" class="w-3 h-3 animate-spin" />
            <span v-else>Save</span>
          </themed-button>
          <themed-button size="sm" variant="secondary" @click="cancelInlineCreate">
            Cancel
          </themed-button>
        </div>
      </div>
      <div v-if="inlineError" class="task-subtasks__inline-error">
        {{ inlineError }}
      </div>
    </div>

    <!-- Subtask List -->
    <div v-if="subtasks.length" class="task-subtasks__list">
      <div
        v-for="subtask in subtasks"
        :key="subtask.id"
        class="task-subtasks__item"
      >
        <div class="task-subtasks__item-left">
          <span class="task-subtasks__badge" :style="{ backgroundColor: getStatusColor(subtask.status) }">
            {{ subtask.status ? subtask.status.name : 'Unknown' }}
          </span>
          <span class="task-subtasks__item-title">
            <a 
              v-if="subtask.task_key" 
              :href="`/tasks/${subtask.task_key}`" 
              target="_blank"
              class="task-subtasks__item-number"
              @click.stop
            >
              {{ subtask.task_key }}
            </a>
            <span @click="$emit('open-subtask', subtask)">{{ subtask.title }}</span>
          </span>
        </div>
        <div class="task-subtasks__item-right">
          <span v-if="subtask.priority" class="task-subtasks__badge" :style="{ backgroundColor: getPriorityColor(subtask.priority) }">
            {{ subtask.priority.name }}
          </span>
          <span class="task-subtasks__item-assignee" v-if="subtask.assignee">
            {{ subtask.assignee.name }}
          </span>
          <span v-if="subtask.due_date" class="task-subtasks__item-date" :class="{ 'task-subtasks__item-date--overdue': subtask.is_overdue }">
            {{ formatDate(subtask.due_date) }}
          </span>
          <div v-if="subtask.comments_count > 0" class="task-subtasks__item-comments" :title="`${subtask.comments_count} comment${subtask.comments_count !== 1 ? 's' : ''}`">
            <theme-icon icon="comment" type="icons" classes="w-4 h-4" />
            <span class="task-subtasks__item-comments-count">{{ subtask.comments_count }}</span>
          </div>
          <button
            v-if="canEdit"
            class="task-subtasks__item-edit"
            @click="$emit('open-subtask', subtask)"
            title="Edit sub-task"
          >
            <theme-icon icon="edit-icon" type="icons" classes="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>

    <div v-else-if="!showInlineCreate" class="task-subtasks__empty">
      No sub-tasks yet.
    </div>

    <!-- Progress Bar -->
    <div v-if="subtasks.length" class="task-subtasks__progress">
      <div class="task-subtasks__progress-bar">
        <div class="task-subtasks__progress-fill" :style="{ width: completionPercent + '%' }"></div>
      </div>
      <span class="task-subtasks__progress-label">{{ completedCount }}/{{ subtasks.length }} completed</span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ThemedButton from '../ThemedButton.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'TaskSubtasks',

  components: {
    ThemedButton,
    ThemedSelect,
    ThemeIcon
  },

  props: {
    taskId: {
      type: [Number, String],
      required: true
    },
    canEdit: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      subtasks: [],
      showInlineCreate: false,
      inlineLoading: false,
      inlineError: null,
      inlineForm: {
        title: '',
        status_id: null,
        priority_id: null
      }
    };
  },

  computed: {
    ...mapGetters('tasks', ['activeStatuses', 'activePriorities']),

    completedCount() {
      return this.subtasks.filter(s => s.status && s.status.name === 'Done').length;
    },

    completionPercent() {
      if (!this.subtasks.length) return 0;
      return Math.round((this.completedCount / this.subtasks.length) * 100);
    },

    statusOptions() {
      return this.activeStatuses.map(s => ({
        label: s.name,
        value: s.id
      }));
    },

    priorityOptions() {
      return this.activePriorities.map(p => ({
        label: p.name,
        value: p.id
      }));
    }
  },

  watch: {
    taskId: {
      immediate: true,
      handler(newId) {
        if (newId) this.fetchSubtasks();
      }
    }
  },

  async mounted() {
    // Load statuses and priorities
    await Promise.all([
      this.$store.dispatch('tasks/fetchStatuses'),
      this.$store.dispatch('tasks/fetchPriorities')
    ]);
  },

  methods: {
    async fetchSubtasks() {
      try {
        const response = await axios.get('/api/tasks', {
          params: { parent_id: this.taskId, per_page: 100 }
        });
        this.subtasks = response.data.data || response.data;
      } catch (error) {
        console.error('Error fetching subtasks:', error);
      }
    },

    startInlineCreate() {
      this.showInlineCreate = true;
      this.inlineError = null;
      
      // Set default status to first status (usually "Planned")
      if (this.statusOptions.length > 0) {
        this.inlineForm.status_id = this.statusOptions[0].value;
      }
      
      // Focus the input field
      this.$nextTick(() => {
        if (this.$refs.inlineNameInput) {
          this.$refs.inlineNameInput.focus();
        }
      });
    },

    cancelInlineCreate() {
      this.showInlineCreate = false;
      this.inlineForm = {
        title: '',
        status_id: null,
        priority_id: null
      };
      this.inlineError = null;
    },

    async saveInlineSubtask() {
      if (!this.inlineForm.title || !this.inlineForm.title.trim()) {
        this.inlineError = 'Task name is required';
        return;
      }

      this.inlineLoading = true;
      this.inlineError = null;

      try {
        const payload = {
          title: this.inlineForm.title.trim(),
          parent_id: this.taskId,
          status_id: this.inlineForm.status_id,
          priority_id: this.inlineForm.priority_id,
          // Set assigned_to to current user by default
          assigned_to: window.user?.id
        };

        const response = await this.$store.dispatch('tasks/createTask', payload);

        this.$notify({
          type: 'success',
          title: 'Success',
          text: 'Sub-task created successfully'
        });

        // Add to list
        this.subtasks.push(response);
        
        // Reset form
        this.cancelInlineCreate();
        
        // Emit event
        this.$emit('subtask-created', response);
      } catch (error) {
        console.error('Error creating subtask:', error);
        this.inlineError = error.response?.data?.message || 'Failed to create sub-task';
        
        this.$notify({
          type: 'error',
          title: 'Error',
          text: this.inlineError
        });
      } finally {
        this.inlineLoading = false;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric'
      });
    },

    getStatusColor(status) {
      if (!status || !status.color) return '#6b7280';
      const colorMap = {
        blue: '#3b82f6',
        yellow: '#eab308',
        green: '#22c55e',
        red: '#ef4444',
        orange: '#f97316',
        gray: '#6b7280'
      };
      return colorMap[status.color] || status.color;
    },

    getPriorityColor(priority) {
      if (!priority || !priority.color) return '#6b7280';
      const colorMap = {
        blue: '#3b82f6',
        yellow: '#eab308',
        green: '#22c55e',
        red: '#ef4444',
        orange: '#f97316',
        gray: '#6b7280'
      };
      return colorMap[priority.color] || priority.color;
    }
  }
};
</script>
